import { type WorkflowEvent } from '@/services/workflow/get-run-events';
import {
  NodeDataType,
  NodeEventType,
  StreamType,
  type NodeEvent,
} from '@/types/workflow-types';

export interface MapThinkingEventsOptions {
  /**
   * Map persisted ``awaiting_intervention`` rows so a pending Human
   * Intervention survives reopening the run (notification deep-link,
   * past-runs sidebar). Only pass ``true`` for live (running/paused)
   * runs — a historical run must not render a live approval banner.
   */
  includeAwaitingIntervention?: boolean;
}

/**
 * Maps persisted WorkflowEvent rows (from ``GET .../runs/{id}/events``) into
 * NodeEvent objects grouped by ``node_instance_id``.
 *
 * Handles the persisted chain-of-thought / lifecycle event types:
 *   - ``thinking``      → NodeDataType.THINKING
 *   - ``agent_message`` → NodeDataType.AGENT_MESSAGE
 *   - ``tool_call``     → NodeDataType.TOOL_CALL
 *   - ``tool_result``   → NodeDataType.TOOL_RESULT
 *   - ``awaiting_intervention`` → NodeDataType.INTERVENTION_REQUEST
 *     (opt-in via ``options.includeAwaitingIntervention``)
 *
 * ``node_result`` rows are intentionally NOT mapped here: callers
 * already synthesise one NODE_RESULT event per node from the
 * authoritative ``run.node_results`` table. Mapping it here too would
 * double-count node outputs (and render each output twice in the
 * Events timeline).
 *
 * Events with a null ``node_instance_id`` or an unrecognised display type are
 * silently skipped so callers never receive invalid events.
 *
 * @param events - Raw WorkflowEvent array from the API
 * @param options - Optional mapping behaviour flags
 * @returns Events grouped by node ID, ready to merge into ``synthesizedStatuses``
 */
export function mapThinkingEvents(
  events: WorkflowEvent[],
  options: MapThinkingEventsOptions = {},
): Record<string, NodeEvent[]> {
  const result: Record<string, NodeEvent[]> = {};

  for (const evt of events) {
    const nodeId = evt.node_instance_id;
    if (!nodeId) continue;

    const eventData = evt.event_data ?? {};
    const nodeEvent = buildNodeEvent(evt, nodeId, eventData, options);
    if (!nodeEvent) continue;

    if (!result[nodeId]) {
      result[nodeId] = [];
    }
    result[nodeId].push(nodeEvent);
  }

  return result;
}

function buildNodeEvent(
  evt: WorkflowEvent,
  nodeId: string,
  eventData: Record<string, unknown>,
  options: MapThinkingEventsOptions,
): NodeEvent | null {
  const base = {
    node_id: nodeId,
    node_display_name: '',
    dynamic_message: '',
    mode: StreamType.BLOCK,
    timestamp: evt.timestamp,
    event_id: evt.id,
    block_id: evt.id,
    event_type: NodeEventType.PROCESSING,
  } as const;

  switch (evt.event_type) {
    case 'thinking': {
      const thought =
        typeof eventData.thought === 'string' ? eventData.thought : '';
      return { ...base, display_type: NodeDataType.THINKING, data: thought };
    }
    case 'agent_message': {
      const message =
        typeof eventData.message === 'string' ? eventData.message : '';
      return {
        ...base,
        display_type: NodeDataType.AGENT_MESSAGE,
        data: message,
      };
    }
    case 'tool_call': {
      return {
        ...base,
        display_type: NodeDataType.TOOL_CALL,
        data: {
          tool_name: eventData.tool_name ?? '',
          tool_arguments: eventData.tool_arguments ?? '',
        },
      };
    }
    case 'tool_result': {
      return {
        ...base,
        display_type: NodeDataType.TOOL_RESULT,
        data: {
          tool_name: eventData.tool_name ?? '',
          tool_result: eventData.tool_result ?? '',
        },
      };
    }
    case 'node_start': {
      return {
        ...base,
        event_type: NodeEventType.BEGIN,
        display_type: NodeDataType.UPDATE,
        node_display_name:
          typeof eventData.display_name === 'string'
            ? eventData.display_name
            : '',
        data: '',
      };
    }
    case 'node_progress': {
      const status =
        typeof eventData.status === 'string' ? eventData.status : '';
      return {
        ...base,
        display_type: NodeDataType.UPDATE,
        dynamic_message: status,
        data: status,
      };
    }
    case 'node_success': {
      return {
        ...base,
        event_type: NodeEventType.EXECUTION_SUCCESSFUL,
        display_type: NodeDataType.UPDATE,
        data: '',
      };
    }
    case 'node_complete': {
      return {
        ...base,
        event_type: NodeEventType.END,
        display_type: NodeDataType.UPDATE,
        node_display_name:
          typeof eventData.display_name === 'string'
            ? eventData.display_name
            : '',
        data: '',
      };
    }
    case 'node_failure': {
      return {
        ...base,
        event_type: NodeEventType.EXECUTION_FAILED,
        display_type: NodeDataType.ERROR,
        data: eventData,
      };
    }
    case 'awaiting_intervention': {
      if (!options.includeAwaitingIntervention) return null;
      const requestId =
        typeof eventData.request_id === 'string' ? eventData.request_id : '';
      if (!requestId) return null;
      return {
        ...base,
        event_type: NodeEventType.AWAITING_INTERVENTION,
        display_type: NodeDataType.INTERVENTION_REQUEST,
        dynamic_message: 'Awaiting user input',
        data: {
          request_id: requestId,
          timeout: eventData.timeout,
        },
      };
    }
    default:
      return null;
  }
}
